import { QueryForOrderPaymentIntentDataOrderItem, QueryForOrderPaymentSuccessDataOrderItem } from "../functions/src/dataConnect/order.queries";
import { Fulfillment, TruncatedOrderItemForFulfillmentUpdateBeforeShipping } from "./fulfillment.types";
import { ListingPublic } from "./listings.types";
import { Discount, OrderItem, OrderOrFulfillment, OrderPublic, StripeOrderTotals } from "./order.types";
import { FulfillmentRefundInfo } from "./refund.types";

export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export interface HandleErrorParams {
  location: string,
  data: any,
  error: any,
  message?: string,
  suppress?: boolean,
  isCritical?: boolean
}

export const stringifyCircular = (obj:any) => {
  const seen = new WeakSet();
  return JSON.stringify(obj, (key, value) => {
    if (typeof value === "object" && value !== null) {
      if (seen.has(value)) {
        return; // Ignore circular references
      }
      seen.add(value);
    }
    return value;
  });
};
export interface ApiResponse {
  success: boolean;
  data: any;
  errors: string[];
}
export interface ApiError {
  location: string;
  data: any;
  date?: Date;
  message: string;
  suppress?: boolean;
  timestamp: number;
  originalError: any;
  isCritical?: boolean;
  stack: string;
}

export const getTotalsFromItems = (
  orderItems: (OrderItem | QueryForOrderPaymentIntentDataOrderItem | QueryForOrderPaymentSuccessDataOrderItem | TruncatedOrderItemForFulfillmentUpdateBeforeShipping)[],
  discounts: Discount[],
): StripeOrderTotals => {
  const totals: StripeOrderTotals = {
    subTotalAmount: 0,
    totalDiscountAmount: 0,
    totalShippingAmount: 0,
    totalFixedShippingAmount: 0,
    totalTaxAmount: 0,
    totalAmount: 0,
    lineItems: [],
  }
  orderItems.forEach((item) => {
    // TODO: discounts & third-party shipping prices
    // if shippingRateId get cost from provider...
    let shipping = 0;
    if(item.rateCost){
      shipping = item.rateCost;
      // TODO: If item has a rateId and a fixedShippingCost from the initial purchase, what should happen to the money paid?
    } else if(item.fixedShippingCost){
      shipping = item.fixedShippingCost;
      totals.totalFixedShippingAmount = (totals.totalFixedShippingAmount || 0) + item.fixedShippingCost;
    }
    let itemsCost = (item.quantity * item.pricePerItemAtTimeOfOrder)
    
    totals.totalShippingAmount = shipping + (totals.totalShippingAmount || 0);
    
    totals.totalAmount = (totals.totalAmount || 0) + itemsCost + shipping;
    totals.subTotalAmount = itemsCost + (totals.subTotalAmount || 0);
    totals.lineItems.push({
      quantity: item.quantity,
      amount: itemsCost,
      tax_code: item.listingVariation.listing?.category?.taxCode || "txcd_99999999",
      reference: item.id,
    })
  })
  let commissionableFunds = (totals.subTotalAmount || 0);
  if(totals.totalFixedShippingAmount && totals.totalFixedShippingAmount > 0){
    commissionableFunds += totals.totalFixedShippingAmount;
  }
  totals.commissionAmount = commissionableFunds / 10; // FIXME Use fulfillment.commissionRatePercent when available [commissionPercent]
  
  return totals;
}

export interface Allotments {
  totalFunds: number;
  promisedFunds: number;
  totalFulfillments: number;
  allocatedFunds: number;
  unconfirmedFulfillments: number;
}
// [x] NEEDS TESTS
export const getOrderAllocatedShippingFunds = (fulfillments: Fulfillment[], fulfillmentId: string): Allotments => {
  fulfillments = fulfillments.filter(f=>!f.fulfillmentFixedShippingAmount)
  const totalFulfillments = fulfillments.filter(f=>f.parcels_on_fulfillment.length > 0 && f.parcels_on_fulfillment.some(p=>p.orderItems_on_parcel.length > 0))
  const totalFunds = Math.round(fulfillments.reduce((acc, f) => acc + f.rateCharged || 0, 0) * 100) / 100
  const promisedFunds = fulfillments.filter(f=>!f.fulfillmentFixedShippingAmount).reduce((acc, f) => acc + (!!f.trackingNumber ? 0 : (!!f.rateCost ? f.rateCost : f.rateCharged)), 0);
  return {
    totalFunds,
    promisedFunds,
    totalFulfillments: totalFulfillments.length,
    allocatedFunds: fulfillments.reduce((acc, f) => acc + (f.id === fulfillmentId ? f.rateCharged : (f.rateCost || 0)), 0), // [ ] check what happens if no new cost is created, does it need a charged backup?
    unconfirmedFulfillments: totalFulfillments.filter((f) => (!f.trackingNumber && !f.rateCost)).length - 1 // the current one
  } as Allotments
}
// [ ] NEEDS TESTS
export const getAllotmentVarsForDoubleCheck = (allotments: Allotments) => {
  const remainingFunds = allotments.totalFunds - allotments.allocatedFunds;
  const fulfillmentsLeft = allotments.totalFulfillments - allotments.unconfirmedFulfillments - 1;
  const vars = {
    remainingFundStatement: 'There ' + (remainingFunds === 0 ? 'are ' : 'will be ') + (remainingFunds < 0 ? 'a debt of ' : ''),
    remainingFunds,
    promisedFunds: allotments.promisedFunds,
    conjunction: fulfillmentsLeft !== 0 ? " left with " : "",
    fulfillmentsLeft,
    fulfillmentsLeftStatement:  (fulfillmentsLeft > 0) ? ' remaining fulfillment' + (fulfillmentsLeft === 1 ? '' : 's'): (remainingFunds  <= 0 ? '' : ' remaining that will be refunded to the customer'),
    showWarning: fulfillmentsLeft === 0 && remainingFunds <= 0,
  }
  if(vars.remainingFunds === 0 && vars.fulfillmentsLeft === 0){
    return undefined;
  }
  return vars;
}

export const getSubdomain = (document: {location: {hostname: string}}): string | null => {
  const hostname = document.location.hostname;
  const parts = hostname.split('.');
  
  // If hostname is just 'localhost' or has no dots, return null
  if (parts.length < 2) return null;
  
  // Return the first part as the subdomain
  return ["dev","staging"].includes(parts[0]) ? null : parts[0];
}

export const getFormattedOrder = (order: OrderOrFulfillment | OrderPublic, group: "location" | "fulfillment", keepInCents = false): OrderOrFulfillment => {
  let formattedOrder: OrderOrFulfillment | OrderPublic = JSON.parse(JSON.stringify(order));
  const orderItems: OrderItem[] = formattedOrder.orderItems_on_order
  formattedOrder = {...formattedOrder} as OrderPublic
  if(!keepInCents) formattedOrder = normalizeMoneyForAnOrder(formattedOrder);
  const byLocation = createGrouping(orderItems, 'location');
  const byFulfillment = createGrouping(orderItems, 'fulfillment');
  const byRefund = createGrouping(orderItems, 'refund');
  const refundsByFulfillment = formattedOrder.fulfillments_on_order.reduce((acc, fulfillment) => {
    const refunds = fulfillment.refunds_on_fulfillment || []
    const refundedItems = refunds.filter(refund => refund.statusId === "refund_completed").flatMap(refund => refund.orderItems_on_refund || []) as OrderItem[];
    const pendingItems = refunds.filter(refund => ["refund_pending", "refund_processing"].includes(refund.statusId)).flatMap(refund => refund.orderItems_on_refund || []) as OrderItem[];
    const remaining = byFulfillment[fulfillment.id]?.filter(item => !refundedItems.some(refundedItem => refundedItem.id === item.id) && !pendingItems.some(pendingItem => pendingItem.id === item.id)) || [];
    const refundedAmount = refunds.reduce((acc, oi) => acc + (oi.amount || 0), 0);
    const pendingAmount = pendingItems.reduce((acc, oi) => acc + (oi.totalCostAtTimeOfOrder || 0), 0); 
    const remainingAmount = remaining.reduce((acc, oi) => acc + (oi.totalCostAtTimeOfOrder || 0), 0);
    const remainingItems = remaining as OrderItem[];
    const info: FulfillmentRefundInfo = {
      refunds, refundedAmount, refundedItems, pendingAmount, pendingItems, remainingAmount, remainingItems
    }
    acc[fulfillment.id] = info;
    return acc;
  }, {} as { [id: string]: FulfillmentRefundInfo })
  if(byLocation) formattedOrder['orderItems_by_location'] = byLocation;
  if(byFulfillment) formattedOrder['orderItems_by_fulfillment'] = byFulfillment;
  if(byRefund) formattedOrder['orderItems_by_refund'] = byRefund;
  formattedOrder.refunds_by_fulfillment = refundsByFulfillment;
  return formattedOrder
}
// [ ] NEEDS TESTS
export const getFormattedFulfillments= (fulfillments: Fulfillment[], keepInCents = false): Fulfillment[] => {
  let formattedFulfillments: Fulfillment[] = JSON.parse(JSON.stringify(fulfillments));

  if(!keepInCents) formattedFulfillments = normalizeMoneyForFulfillments(formattedFulfillments);
  return formattedFulfillments
}

const normalizeMoneyForAnOrder = (order: OrderOrFulfillment): OrderOrFulfillment => {
  order.orderItems_on_order.forEach(item => {
    item.totalCostAtTimeOfOrder /= 100
    item.rateCost = item.rateCost ? item.rateCost / 100 : undefined;
    item.rateCharged = item.rateCharged ? item.rateCharged / 100 : undefined;
    item.pricePerItemAtTimeOfOrder /= 100
    item.totalTax = (item.totalTax || 0) / 100
    if(item.fixedShippingCost) item.fixedShippingCost = item.fixedShippingCost / 100;
  })
  order.totalDiscountAmount = (order.totalDiscountAmount || 0) / 100
  order.totalShippingAmount = (order.totalShippingAmount || 0) / 100
  order.shippingTaxAmount = (order.shippingTaxAmount || 0) / 100
  order.totalTaxAmount = (order.totalTaxAmount || 0) / 100
  order.subTotalAmount = (order.subTotalAmount || 0) / 100;
  order.totalFixedShippingAmount = (order.totalFixedShippingAmount || 0) / 100;
  order.totalAmount = (order.totalAmount || 0) / 100;
  order.fulfillments_on_order && order.fulfillments_on_order.forEach(fulfillment => {
    fulfillment.refunds_on_fulfillment && fulfillment.refunds_on_fulfillment.forEach(refund => {
      refund.amount = refund.amount / 100;
      refund.tax = refund.tax / 100;
    })
  })
  if(order.marketPlaceCommission) order.marketPlaceCommission = order.marketPlaceCommission / 100;
  return order;
}

const normalizeMoneyForFulfillments = (fulfillments: Fulfillment[]): Fulfillment[] => {
  fulfillments.forEach(fulfillment=>{
    fulfillment.parcels_on_fulfillment.forEach(parcel => {
      parcel.orderItems_on_parcel.forEach(item => {
        item.totalCostAtTimeOfOrder /= 100
        item.rateCost = item.rateCost ? item.rateCost / 100 : undefined;
        item.rateCharged = item.rateCharged ? item.rateCharged / 100 : undefined;
        item.pricePerItemAtTimeOfOrder /= 100
        item.totalTax = (item.totalTax || 0) / 100
      })
    })
    if(fulfillment.refunds_on_fulfillment) fulfillment.refunds_on_fulfillment.forEach(refund => {
      refund.amount = refund.amount / 100;
      refund.tax = refund.tax / 100;
    })
    fulfillment.fulfillmentDiscountAmount = (fulfillment.fulfillmentDiscountAmount || 0)/100
    fulfillment.fulfillmentTotalAmount = (fulfillment.fulfillmentTotalAmount || 0)/100
    fulfillment.fulfillmentSalesTaxAmount = (fulfillment.fulfillmentSalesTaxAmount || 0)/100
    fulfillment.fulfillmentSubTotalAmount = (fulfillment.fulfillmentSubTotalAmount || 0)/100
    fulfillment.fulfillmentFixedShippingAmount = (fulfillment.fulfillmentFixedShippingAmount || 0)/100
    fulfillment.rateCharged = (fulfillment.rateCharged || 0)/100
    fulfillment.rateCost = (fulfillment.rateCost || 0)/100
    if(fulfillment.commissionAmount) fulfillment.commissionAmount = fulfillment.commissionAmount / 100;
  })
  return fulfillments;
}

const createGrouping = (orderItems: OrderItem[], group: "location" | "fulfillment" | "refund" ) => {
  const groupedOrderItems: { [id: string]: OrderItem[] } = {};
  orderItems.forEach((item: OrderItem ) => {
    let id
    switch(group){
      case "location":
        if(!item.listingVariation?.listing?.locationId && !item.listingVariation?.listing?.location?.id){
          console.warn("Order item without locationId", {item})
          return;
        }
        const listing = item.listingVariation?.listing;
        const FIXME_listing = item.listingVariation?.listing as unknown as { location: { id: string } }
        id = listing?.locationId || FIXME_listing?.location.id ;
        break;
      case "fulfillment":
        id = item.fulfillmentId;
        break;
      case "refund":        
        id = item.refundId;
        break;
    }
    if (!!id &&!groupedOrderItems[id]) {
      groupedOrderItems[id] = [];
    }
    if(!!id) groupedOrderItems[id].push(item);
  });

  // Now, sort each group by createdAt
  for (const id in groupedOrderItems) {
    groupedOrderItems[id].sort((a, b) => {
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });
  }
  return groupedOrderItems;
}

export const normalizeShippingDimensionsFromListing = <T>(
  item: T,
  lengthProp: keyof T,
  widthProp: keyof T,
  heightProp: keyof T,
  quantity: number = 1,
  compressionRate: number = 0,
): T => {
  let height = item[heightProp] as string;
  const compressionMultiplier = quantity > 1 && compressionRate > 0 ? (1 - (compressionRate / 100)) : 1;
  item[heightProp] = (parseFloat(height as unknown as string) * compressionMultiplier) as T[typeof heightProp];
  item = {...item, height: item[heightProp]+"" as string} as T;
  return normalizeShippingDimensions<T>(item, lengthProp, widthProp, heightProp);
}

export const normalizeShippingDimensions = <T>(
  parcel: T,
  lengthProp: keyof T,
  widthProp: keyof T,
  heightProp: keyof T,
):T => {
  const lengthNumber = parseFloat(parcel[lengthProp] as unknown as string) || 0;
  const widthNumber = parseFloat(parcel[widthProp] as unknown as string) || 0;
  const heightNumber = parseFloat(parcel[heightProp] as unknown as string) || 0;
  if(isNaN(lengthNumber) || isNaN(widthNumber) || isNaN(heightNumber)){
    return parcel
  }
  const longestProp = [lengthProp, widthProp, heightProp][[lengthNumber, widthNumber, heightNumber].indexOf(Math.max(lengthNumber, widthNumber, heightNumber))];
  const shortestProp = [lengthProp, widthProp, heightProp][[lengthNumber, widthNumber, heightNumber].indexOf(Math.min(lengthNumber, widthNumber, heightNumber))];
  if(parcel[longestProp] === parcel[shortestProp]){
    return parcel
  }
  const middleProp = [lengthProp, widthProp, heightProp].find(prop => prop !== longestProp && prop !== shortestProp)!;
  const lengthValue = parcel[longestProp];
  const widthValue = parcel[middleProp];
  const heightValue = parcel[shortestProp];
  parcel[lengthProp] = lengthValue;
  parcel[widthProp] = widthValue;
  parcel[heightProp] = heightValue;
  return parcel
}


export interface ListingShippingCost {shippingCost: number | null; shippingAdditionalCost: number }
export const getShippingCostFromListing = (listing: ListingPublic | Partial<ListingPublic>, inCents = false): ListingShippingCost => {
  if(!listing || !listing.shippingType || !["Free Shipping", "Fixed Price", "Digital Download"].includes(listing.shippingType)) return {  shippingCost: null, shippingAdditionalCost: 0 };
  const shippingCost = (listing.shippingCost || 0) * (inCents ? 100 : 1);
  const shippingAdditionalCost = !listing.shippingAdditionalCost || isNaN(listing.shippingAdditionalCost) ? shippingCost : (listing.shippingAdditionalCost || 0) * (inCents ? 100 : 1) || 0;
  return { shippingCost, shippingAdditionalCost };
}   
